import { useCallback, useState } from 'react'
import isEqual from 'lodash/isEqual'
import { useReferredValue } from '@manychat/manyui'

import { useGoalEditorContext } from 'apps/aiAutomation/components/EngagementGoalEditor/lib/context'
import {
  LeadGenEditorStep,
  StepState,
  SubmitResult,
} from 'apps/aiAutomation/components/EngagementGoalEditor/lib/types'
import { GoalType, LeadGenGoal } from 'shared/api/requests/aiEngagementGoal/schemas'

import {
  createGoalFromFormValues,
  mapGoalToContextFormValues,
  mapGoalToMessageFormValues,
  mergeContextValuesWithGoal,
  mergeMessageValuesWithGoal,
} from '../lib/mappers'
import { ContextEditorFormValues, MessageEditorFormValues } from '../lib/types'

export const useLeadGenEditor = () => {
  const { goalId, goal, save } = useGoalEditorContext(GoalType.LEAD_GEN)

  const [contextData, setContextData] = useState<ContextEditorFormValues | null>(
    goal ? mapGoalToContextFormValues(goal) : null,
  )
  const [contentData, setContentData] = useState<MessageEditorFormValues | null>(
    goal ? mapGoalToMessageFormValues(goal) : null,
  )

  const referredContextData = useReferredValue(contextData)
  const referredContentData = useReferredValue(contentData)

  const [stepsState, setStepsState] = useState<StepState<LeadGenEditorStep>>({
    current: goal ? LeadGenEditorStep.PUBLISH : LeadGenEditorStep.CONTEXT,
    completed: {
      [LeadGenEditorStep.CONTEXT]: Boolean(goal),
      [LeadGenEditorStep.MESSAGE]: Boolean(goal),
      [LeadGenEditorStep.PUBLISH]: false,
    },
  })

  const openStep = useCallback((step: LeadGenEditorStep) => {
    setStepsState((prev) => ({
      ...prev,
      current: step,
    }))
  }, [])

  const completeStep = useCallback((step: LeadGenEditorStep) => {
    let nextStep: LeadGenEditorStep

    switch (step) {
      case LeadGenEditorStep.CONTEXT: {
        nextStep = LeadGenEditorStep.MESSAGE
        break
      }
      case LeadGenEditorStep.MESSAGE: {
        nextStep = LeadGenEditorStep.PUBLISH
        break
      }
      case LeadGenEditorStep.PUBLISH: {
        nextStep = LeadGenEditorStep.PUBLISH
        break
      }
    }

    setStepsState((prev) => ({
      ...prev,
      completed: {
        ...prev.completed,
        [step]: true,
      },
      current: nextStep,
    }))
  }, [])

  const onContextFinish = useCallback(
    (data: ContextEditorFormValues) => {
      setContextData(data)

      if (!goal) {
        completeStep(LeadGenEditorStep.CONTEXT)
      }
    },
    [completeStep, goal],
  )

  const onContentFinish = useCallback(
    (data: MessageEditorFormValues) => {
      setContentData(data)

      completeStep(LeadGenEditorStep.MESSAGE)

      if (!goal) {
        save(
          createGoalFromFormValues({
            id: goalId,
            values: {
              ...(referredContextData.current as ContextEditorFormValues),
              ...data,
            },
          }),
        )
        return
      }

      if (!isEqual(data, referredContentData.current)) {
        save(mergeMessageValuesWithGoal(goal, data))
      }
    },
    [completeStep, goal, goalId, save, referredContentData, referredContextData],
  )

  const combineAllData: (
    context: SubmitResult<ContextEditorFormValues>,
    message: SubmitResult<MessageEditorFormValues>,
  ) => SubmitResult<LeadGenGoal> = useCallback(
    (context, message) => {
      if (!goal || !context.isValid || !message.isValid) {
        return { isValid: false }
      }

      openStep(LeadGenEditorStep.PUBLISH)

      return {
        isValid: true,
        data: mergeMessageValuesWithGoal(
          mergeContextValuesWithGoal(goal, context.data),
          message.data,
        ),
      }
    },
    [goal, openStep],
  )

  return {
    stepsState,
    contextData,
    contentData,
    onContextFinish,
    onContentFinish,
    combineAllData,
  }
}
